import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { userAPI } from '@/services/api';
import { motion } from 'framer-motion';
import { 
  Wallet, 
  TrendingUp, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  DollarSign,
  Activity,
  Clock,
  ChevronRight,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import LiveProfitTracker from '@/components/LiveProfitTracker';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await userAPI.getDashboard();
        setDashboard(response.data);
        refreshUser();
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const stats = [
    {
      label: 'Available Balance',
      value: `$${user?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`,
      icon: Wallet,
      color: 'emerald',
      change: '+0%'
    },
    {
      label: 'Active Trading',
      value: `$${user?.invested_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`,
      icon: TrendingUp,
      color: 'cyan',
      change: dashboard?.recent_investments?.length ? 'Trading' : 'Inactive'
    },
    {
      label: 'Total Profit',
      value: `$${user?.total_profit?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`,
      icon: DollarSign,
      color: 'emerald',
      change: '+2% daily'
    },
    {
      label: 'Account Status',
      value: user?.tier || 'Basic',
      icon: Activity,
      color: 'cyan',
      change: 'Active'
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-card p-3 text-sm">
          <p className="text-white/60">{label}</p>
          <p className="text-emerald-400 font-mono font-bold">
            +${payload[0].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const profitData = dashboard?.profit_history?.map((p, index) => ({
    date: format(new Date(p.date), 'MMM dd'),
    profit: p.amount
  })).reverse() || [];

  return (
    <motion.div 
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-4 sm:space-y-6"
      data-testid="user-dashboard"
    >
      {/* Header */}
      <motion.div variants={fadeInUp}>
        <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white">
          Welcome back, <span className="gradient-text">{user?.name?.split(' ')[0]}</span>
        </h1>
        <p className="text-white/60 mt-1 text-sm sm:text-base">Here's your portfolio overview</p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div 
        variants={stagger}
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      >
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            className="stat-card group"
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                stat.color === 'cyan' 
                  ? 'bg-cyan-500/20 group-hover:bg-cyan-500/30' 
                  : 'bg-emerald-500/20 group-hover:bg-emerald-500/30'
              } transition-colors`}>
                <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${
                  stat.color === 'cyan' ? 'text-cyan-400' : 'text-emerald-400'
                }`} />
              </div>
              <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${
                stat.change.startsWith('+') || stat.change === 'Trading' || stat.change === 'Active'
                  ? 'bg-emerald-400/20 text-emerald-400' 
                  : 'bg-white/10 text-white/50'
              }`}>
                {stat.change}
              </span>
            </div>
            <p className="text-white/60 text-xs sm:text-sm mb-1">{stat.label}</p>
            <p className="text-lg sm:text-2xl font-mono font-bold text-white truncate">{stat.value}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Live Profit Tracker */}
      <LiveProfitTracker />

      {/* Chart & Actions */}
      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Profit Chart */}
        <motion.div 
          variants={fadeInUp}
          className="lg:col-span-2 glass-card p-4 sm:p-6"
        >
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white">Trading Performance</h2>
              <p className="text-white/50 text-xs sm:text-sm">Your automated earnings over time</p>
            </div>
          </div>
          
          {profitData.length > 0 ? (
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={profitData}>
                  <defs>
                    <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00FF94" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00FF94" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.3)"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                    tickFormatter={(value) => `$${value}`}
                    width={45}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="profit" 
                    stroke="#00FF94" 
                    strokeWidth={2}
                    fill="url(#profitGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 sm:h-64 flex items-center justify-center text-white/40 text-sm sm:text-base text-center px-4">
              <div>
                <Zap className="w-10 h-10 mx-auto mb-3 text-white/20" />
                <p>No trading history yet. Fund your account to start automated trading!</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div 
          variants={fadeInUp}
          className="glass-card p-4 sm:p-6"
        >
          <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-4 sm:mb-6">Quick Actions</h2>
          <div className="space-y-3">
            <Button 
              className="w-full justify-between bg-cyan-500/10 hover:bg-cyan-500/20 active:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 h-auto py-3 px-4 touch-manipulation"
              onClick={() => navigate('/dashboard/deposits')}
              data-testid="quick-deposit-btn"
            >
              <span className="flex items-center gap-2 text-sm">
                <ArrowDownToLine className="w-4 h-4 sm:w-5 sm:h-5" />
                Deposit Funds
              </span>
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button 
              className="w-full justify-between bg-emerald-500/10 hover:bg-emerald-500/20 active:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 h-auto py-3 px-4 touch-manipulation"
              onClick={() => navigate('/dashboard/investments')}
              data-testid="quick-trading-btn"
            >
              <span className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                Trading Plans
              </span>
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button 
              className="w-full justify-between bg-white/5 hover:bg-white/10 active:bg-white/15 text-white/70 border border-white/10 h-auto py-3 px-4 touch-manipulation"
              onClick={() => navigate('/dashboard/withdrawals')}
              data-testid="quick-withdraw-btn"
            >
              <span className="flex items-center gap-2 text-sm">
                <ArrowUpFromLine className="w-4 h-4 sm:w-5 sm:h-5" />
                Withdraw Funds
              </span>
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div 
        variants={fadeInUp}
        className="glass-card p-4 sm:p-6"
      >
        <h2 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-4 sm:mb-6">Recent Activity</h2>
        <div className="space-y-3 sm:space-y-4">
          {[...(dashboard?.recent_deposits || []), ...(dashboard?.recent_withdrawals || []), ...(dashboard?.recent_investments || [])]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
            .map((activity, index) => {
              const isDeposit = 'crypto_type' in activity && !('wallet_address' in activity && activity.wallet_address?.length > 30);
              const isWithdrawal = 'wallet_address' in activity && activity.wallet_address?.length > 30;
              const isTrading = 'daily_rate' in activity;
              
              return (
                <div key={index} className="flex items-center gap-3 sm:gap-4 py-3 border-b border-white/5 last:border-0">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${
                    isDeposit ? 'bg-cyan-500/20' : 
                    isWithdrawal ? 'bg-hotpink-400/20' : 
                    'bg-emerald-500/20'
                  }`}>
                    {isDeposit ? <ArrowDownToLine className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" /> :
                     isWithdrawal ? <ArrowUpFromLine className="w-4 h-4 sm:w-5 sm:h-5 text-hotpink-400" /> :
                     <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm sm:text-base truncate">
                      {isDeposit ? `Deposit (${activity.crypto_type})` :
                       isWithdrawal ? `Withdrawal (${activity.crypto_type})` :
                       'Trading Position'}
                    </p>
                    <p className="text-white/50 text-xs sm:text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(activity.created_at), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-mono font-bold text-sm sm:text-base ${
                      isWithdrawal ? 'text-hotpink-400' : 'text-emerald-400'
                    }`}>
                      {isWithdrawal ? '-' : '+'}${activity.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                    <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full inline-block ${
                      activity.status === 'approved' || activity.status === 'completed' || activity.status === 'active'
                        ? 'bg-emerald-400/20 text-emerald-400'
                        : activity.status === 'rejected'
                        ? 'bg-hotpink-400/20 text-hotpink-400'
                        : 'bg-yellow-400/20 text-yellow-400'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              );
            })}
          {(!dashboard?.recent_deposits?.length && !dashboard?.recent_withdrawals?.length && !dashboard?.recent_investments?.length) && (
            <p className="text-white/40 text-center py-8 text-sm sm:text-base">No recent activity</p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
